import lodash from 'lodash'
import type { FormatDate, ObjectRecord } from '@/models'

const $_ = lodash

function getAsset({ asset, type }: { asset: string; type: string }): string {
  return `${process.env.NUXT_API_ASSET_BASE_URL}/${type}/${asset}`
}

function hasKey(obj: ObjectRecord = {}): boolean {
  return Object.keys(obj).length > 0
}

const turkishToEnglish = (text: string): string => {
  if (!text) return ''
  return text
    .replaceAll('Ğ', 'G')
    .replaceAll('Ü', 'U')
    .replaceAll('Ş', 'S')
    .replaceAll('İ', 'I')
    .replaceAll('Ö', 'O')
    .replaceAll('Ç', 'C')
    .replaceAll('ğ', 'g')
    .replaceAll('ü', 'u')
    .replaceAll('ş', 's')
    .replaceAll('ı', 'i')
    .replaceAll('ö', 'o')
    .replaceAll('ç', 'c')
}

function deepMerge(target: ObjectRecord, source: ObjectRecord): any {
  for (const key of Object.keys(source)) {
    if (
      source[key] instanceof Object &&
      source[key] !== null &&
      !Array.isArray(source[key]) &&
      !$_.isEmpty(source[key])
    ) {
      target[key] = deepMerge(target[key] || {}, source[key])
    } else if (!$_.isEmpty(source[key])) {
      target[key] = source[key]
    }
  }
  return target
}

const useSticky = (top: number) => {
  const isSticky = ref(false)

  const onScroll = () => {
    isSticky.value = window.scrollY > top
  }

  onMounted(() => {
    if (import.meta.client) {
      window.addEventListener('scroll', onScroll)
    }
  })

  onUnmounted(() => {
    if (import.meta.client) {
      window.removeEventListener('scroll', onScroll)
    }
  })

  return { isSticky }
}

const formatNumber = ({ num = 0, precision = 2, abs = false }: ObjectRecord): number | string => {
  if (!num) return num
  if (abs) num = Math.abs(num)
  const val = Number(num)
    .toFixed(precision + 2)
    .slice(0, -2)

  return Number(val).toLocaleString('en', {
    minimumFractionDigits: precision,
  })
}

const formatPercent = ({ num = 0, precision = 1 }: ObjectRecord): string | number => {
  const options: Intl.NumberFormatOptions = {
    style: 'percent',
    maximumFractionDigits: 2,
    minimumFractionDigits: precision,
  }
  // Locale was previously read via a `userLocale()` helper that no longer
  // exists; the rest of this file already hardcodes Turkish (formatDate
  // below), so keep the same default here.
  return new Intl.NumberFormat('tr-TR', options).format(Math.abs(Number(num) / 100))
}

const mobileValidator = (text: string) => {
  return text.replace(/\D/g, '').slice(2)
}

const formatDate = ({ date, format = 'date', persistent = false }: FormatDate): string => {
  const currentLang = 'tr'
  const dateFormatter = (f: any) => new Intl.DateTimeFormat(currentLang, f).format(new Date(date))
  const shortDate = (f: any) => {
    return new Date(f).toISOString().split('T')[0]
  }
  const dateTimestamp = (f: any) => {
    return new Date(f).getTime()
  }

  const formats = {
    date: { year: 'numeric', month: '2-digit', day: '2-digit' },
    hour: { hour: '2-digit', minute: '2-digit', second: '2-digit' },
    month: { year: 'numeric', month: 'long', day: 'numeric' },
    monthShort: { month: '2-digit' },
    hourMinutes: { hour: '2-digit', minute: '2-digit' },
    'DD MMM': { day: '2-digit', month: 'long' },
    'DD MM': { day: '2-digit', month: 'short' },
    year: { year: 'numeric' },
  }

  const dates = {
    date: dateFormatter(formats.date),
    hour: dateFormatter(formats.hour),
    long: `${dateFormatter(formats.date)} ${dateFormatter(formats.hour)}`,
    month: dateFormatter(formats.month),
    'DD MMM': dateFormatter(formats['DD MMM']),
    'DD MM': dateFormatter(formats['DD MM']),
    hourMinutes: dateFormatter(formats.hourMinutes),
    longMonth: `${dateFormatter(formats.month)}, ${dateFormatter(formats.hourMinutes)}`,
    shortDate: shortDate(date),
    timestamp: dateTimestamp(date),
  }

  // `dates` is keyed by FormatDate.format; `timestamp` returns a number
  // but every caller in the codebase passes a string-returning format and
  // treats the result as a string, so cast at the boundary.
  if (persistent) return String(dates[format as keyof typeof dates])

  if (['hour', 'date'].includes(format)) {
    const midnight = new Date()
    midnight.setHours(0, 0, 0, 0)
    if (new Date(date) < midnight) format = 'date'
  }

  return String(dates[format as keyof typeof dates])
}

const numberWithSymbol = ({
  num,
  precision = 1,
}: {
  num: string | number | any
  precision: number
}): string => {
  if (!num || num === 0) return '0'
  const symbolBox = [
    { value: 1e18, symbol: 'E' },
    { value: 1e15, symbol: 'P' },
    { value: 1e12, symbol: 'T' },
    { value: 1e9, symbol: 'B' },
    { value: 1e6, symbol: 'M' },
    { value: 1e3, symbol: 'K' },
    { value: 1, symbol: '' },
    { value: 0.001, symbol: '' },
  ]

  num = num.toString().replace(/[^0-9.]/g, '')
  const index = symbolBox.findIndex(({ value }) => num >= value)
  const value = symbolBox[index]?.value
  const symbol = symbolBox[index]?.symbol

  if (num < 1) return Number(num).toFixed(2)
  return (Number(num) / value).toFixed(precision) + symbol
}

const sanitizePriceInput = (value: string) => value.replace(/[^0-9.,]/g, '')

const HTML_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: '\u00A0',
}

const HTML_ENTITY_REGEX = /&(#\d+|#[xX][0-9a-fA-F]+|amp|lt|gt|quot|apos|nbsp);/g

// Turn HTML character references back into the characters they stand for.
// For text that is *printed as text* — `{{ }}`, `v-text`, or markdown that
// escapes on its own — an entity left in place reaches the user as a literal
// `&gt;` instead of `>`.
const decodeHtmlEntities = (text: string): string => {
  if (!text || !text.includes('&')) return text
  // A single pass, deliberately: a double-encoded `&amp;gt;` decodes to
  // `&gt;` and stops there rather than collapsing all the way to `>`.
  return text.replace(HTML_ENTITY_REGEX, (match, ref: string) => {
    if (ref[0] !== '#') return HTML_ENTITIES[ref] ?? match
    const code =
      ref[1] === 'x' || ref[1] === 'X'
        ? Number.parseInt(ref.slice(2), 16)
        : Number.parseInt(ref.slice(1), 10)
    // Out of Unicode's range, or a lone surrogate — fromCodePoint would
    // throw on the former and emit an unpaired surrogate for the latter.
    if (!Number.isFinite(code) || code < 0 || code > 0x10ffff) return match
    if (code >= 0xd800 && code <= 0xdfff) return match
    return String.fromCodePoint(code)
  })
}

export function baseDec(value: string): string {
  try {
    const a = atob(value)
    return atob(a.slice(0, a.length / 2))
  } catch (error) {
    return value
  }
}

export {
  $_,
  hasKey,
  getAsset,
  deepMerge,
  useSticky,
  formatDate,
  formatNumber,
  formatPercent,
  mobileValidator,
  numberWithSymbol,
  turkishToEnglish,
  sanitizePriceInput,
  decodeHtmlEntities,
}
